import type {
  SoapWorkbenchConfigResponse,
  SoapWorkbenchControl,
  SoapWorkbenchMaxOutputTokens,
  SoapWorkbenchModelKey,
  SoapWorkbenchUsage,
} from "../../../shared/soap-workbench";

const MAX_RUN_HISTORY = 10;

export interface SoapWorkbenchForm {
  transcript: string;
  appointmentNote: string;
  priorSoapNote: string;
  prompt: string;
  soapTemplate: string;
  appointmentNoteEnabled: boolean;
  priorSoapNoteEnabled: boolean;
  modelKey: SoapWorkbenchModelKey;
  selections: Record<SoapWorkbenchModelKey, {
    maxOutputTokens: SoapWorkbenchMaxOutputTokens;
    control: SoapWorkbenchControl;
  }>;
}

export type SoapWorkbenchRunStatus = "generating" | "completed" | "failed" | "cancelled";

export interface SoapWorkbenchRun {
  id: string;
  number: number;
  status: SoapWorkbenchRunStatus;
  output: string;
  modelLabel: string;
  effectiveSetting: string;
  maxOutputTokens: number;
  sourceSummary: string;
  wordCount: number;
  formRevision: number;
  startedAt: number;
  endToEndDurationMs: number | null;
  usage: SoapWorkbenchUsage;
  errorMessage: string | null;
}

export interface SoapWorkbenchState {
  form: SoapWorkbenchForm;
  formRevision: number;
  runs: SoapWorkbenchRun[];
  activeRunId: string | null;
  nextRunNumber: number;
}

function defaults(config: SoapWorkbenchConfigResponse["data"]): SoapWorkbenchForm["selections"] {
  return Object.fromEntries(config.models.map((model) => [
    model.key,
    {
      maxOutputTokens: model.defaultMaxOutputTokens,
      control: model.provider === "bedrock"
        ? { kind: "bedrock", mode: "adaptive", effort: model.control.default.effort }
        : { kind: "openai", reasoningEffort: model.control.default.reasoningEffort },
    },
  ])) as SoapWorkbenchForm["selections"];
}

export function createSoapWorkbenchState(config: SoapWorkbenchConfigResponse["data"]): SoapWorkbenchState {
  return {
    form: {
      transcript: "",
      appointmentNote: "",
      priorSoapNote: "",
      prompt: config.defaultPrompt,
      soapTemplate: config.defaultSoapTemplate,
      appointmentNoteEnabled: false,
      priorSoapNoteEnabled: false,
      modelKey: config.defaultModelKey,
      selections: defaults(config),
    },
    formRevision: 0,
    runs: [],
    activeRunId: null,
    nextRunNumber: 1,
  };
}

export type SoapWorkbenchAction =
  | { type: "field"; field: "transcript" | "appointmentNote" | "priorSoapNote" | "prompt" | "soapTemplate"; value: string }
  | { type: "optional_source"; source: "appointmentNote" | "priorSoapNote"; enabled: boolean }
  | { type: "model"; modelKey: SoapWorkbenchModelKey }
  | {
      type: "selection";
      modelKey: SoapWorkbenchModelKey;
      selection: SoapWorkbenchForm["selections"][SoapWorkbenchModelKey];
    }
  | { type: "start"; run: Omit<SoapWorkbenchRun, "number" | "status" | "output" | "endToEndDurationMs" | "usage" | "errorMessage"> }
  | { type: "started"; runId: string; effectiveSetting: string }
  | { type: "delta"; runId: string; delta: string }
  | { type: "completed"; runId: string; endToEndDurationMs: number; usage: SoapWorkbenchUsage; effectiveSetting: string }
  | { type: "failed"; runId: string; endToEndDurationMs: number; message: string }
  | { type: "cancelled"; runId: string; endToEndDurationMs: number }
  | { type: "clear"; config: SoapWorkbenchConfigResponse["data"] };

function updateRun(state: SoapWorkbenchState, runId: string, update: (run: SoapWorkbenchRun) => SoapWorkbenchRun) {
  return { ...state, runs: state.runs.map((run) => run.id === runId ? update(run) : run) };
}

export function soapWorkbenchReducer(state: SoapWorkbenchState, action: SoapWorkbenchAction): SoapWorkbenchState {
  switch (action.type) {
    case "field":
      return { ...state, formRevision: state.formRevision + 1, form: { ...state.form, [action.field]: action.value } };
    case "optional_source":
      return {
        ...state,
        formRevision: state.formRevision + 1,
        form: {
          ...state.form,
          [`${action.source}Enabled`]: action.enabled,
          ...(!action.enabled ? { [action.source]: "" } : {}),
        },
      };
    case "model":
      return { ...state, formRevision: state.formRevision + 1, form: { ...state.form, modelKey: action.modelKey } };
    case "selection":
      return {
        ...state,
        formRevision: state.formRevision + 1,
        form: { ...state.form, selections: { ...state.form.selections, [action.modelKey]: action.selection } },
      };
    case "start": {
      if (state.activeRunId) return state;
      const run: SoapWorkbenchRun = {
        ...action.run,
        number: state.nextRunNumber,
        status: "generating",
        output: "",
        endToEndDurationMs: null,
        usage: { inputTokens: null, outputTokens: null },
        errorMessage: null,
      };
      return {
        ...state,
        activeRunId: run.id,
        nextRunNumber: state.nextRunNumber + 1,
        runs: [run, ...state.runs].slice(0, MAX_RUN_HISTORY),
      };
    }
    case "started":
      return updateRun(state, action.runId, (run) => ({ ...run, effectiveSetting: action.effectiveSetting }));
    case "delta":
      return updateRun(state, action.runId, (run) => ({ ...run, output: run.output + action.delta }));
    case "completed":
      return {
        ...updateRun(state, action.runId, (run) => ({
          ...run,
          status: "completed",
          endToEndDurationMs: action.endToEndDurationMs,
          usage: action.usage,
          effectiveSetting: action.effectiveSetting,
        })),
        activeRunId: state.activeRunId === action.runId ? null : state.activeRunId,
      };
    case "failed":
      return {
        ...updateRun(state, action.runId, (run) => ({
          ...run,
          status: "failed",
          output: "",
          endToEndDurationMs: action.endToEndDurationMs,
          errorMessage: action.message,
        })),
        activeRunId: state.activeRunId === action.runId ? null : state.activeRunId,
      };
    case "cancelled":
      return {
        ...updateRun(state, action.runId, (run) => ({
          ...run,
          status: "cancelled",
          output: "",
          endToEndDurationMs: action.endToEndDurationMs,
        })),
        activeRunId: state.activeRunId === action.runId ? null : state.activeRunId,
      };
    case "clear":
      return createSoapWorkbenchState(action.config);
  }
}
